import { DollarSign } from "lucide-react";
import { useGetIdentity, useGetList, useTranslate } from "ra-core";
import { Link } from "react-router";
import { ReferenceField } from "@/components/admin/reference-field";
import { Card } from "@/components/ui/card";

import { SimpleList } from "../simple-list/SimpleList";
import { CompanyAvatar } from "../companies/CompanyAvatar";
import { findDealLabel } from "../deals/dealUtils";
import { isActiveProjectStage } from "../deals/stages";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal } from "../types";

/**
 * This component displays the projects pipeline for the current user.
 * It's currently not used in the application but can be added to the dashboard.
 */
export const DealsPipeline = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { projectStages, currency } = useConfigurationContext();
  const { data, total, isPending } = useGetList<Deal>(
    "projects",
    {
      pagination: { page: 1, perPage: 10 },
      sort: { field: "updated_at", order: "DESC" },
      filter: { sales_id: identity?.id },
    },
    { enabled: Number.isInteger(identity?.id) },
  );

  const getOrderedDeals = (data?: Deal[]): Deal[] | undefined => {
    if (!data) {
      return;
    }
    const deals: Deal[] = [];
    projectStages
      .filter((stage) => isActiveProjectStage(stage.value))
      .forEach((stage) =>
        data
          .filter((deal) => deal.stage === stage.value)
          .forEach((deal) => deals.push(deal)),
      );
    return deals;
  };

  return (
    <>
      <div className="flex items-center mb-4">
        <div className="ml-8 mr-8 flex">
          <DollarSign className="text-muted-foreground w-6 h-6" />
        </div>
        <Link
          className="text-xl font-semibold text-muted-foreground hover:underline"
          to="/projects"
        >
          {translate("crm.dashboard.projects_pipeline")}
        </Link>
      </div>
      <Card>
        <SimpleList<Deal>
          resource="projects"
          linkType="show"
          data={getOrderedDeals(data)}
          total={total}
          isPending={isPending}
          primaryText={(deal) => deal.name}
          secondaryText={(deal) =>
            `${(deal.contract_amount ?? deal.amount ?? 0).toLocaleString(
              "en-US",
              {
                notation: "compact",
                style: "currency",
                currency,
                currencyDisplay: "narrowSymbol",
                minimumSignificantDigits: 3,
              },
            )} , ${findDealLabel(projectStages, deal.stage)}`
          }
          leftAvatar={(deal) => (
            <ReferenceField
              source="company_id"
              record={deal}
              reference="companies"
              resource="projects"
              link={false}
            >
              <CompanyAvatar width={20} height={20} />
            </ReferenceField>
          )}
        />
      </Card>
    </>
  );
};
